import { useState } from "react";
import { getAdminEmail, setAdminEmail, adminFetch } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { UserCog, KeyRound, Loader2, Info } from "lucide-react";

function validateProfileForm(email: string, password?: string, confirmPassword?: string): string | null {
  if (email && !email.endsWith("@ugurcanuzunkaya.com")) {
    return "Email must end with @ugurcanuzunkaya.com";
  }

  if (password) {
    if (password !== confirmPassword) {
      return "New passwords do not match";
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
      return "New password must be at least 8 characters long, and contain at least one uppercase, lowercase, number, and special character.";
    }
  }

  return null;
}

export function AdminProfileTab() {
  const currentEmail = getAdminEmail();
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [updating, setUpdating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword) {
      toast.error("Current password is required to verify changes");
      return;
    }

    const validationError = validateProfileForm(newEmail, newPassword, confirmNewPassword);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setUpdating(true);
    try {
      await adminFetch("/api/admin/profile", {
        currentPassword,
        newEmail: newEmail.trim() || undefined,
        newPassword: newPassword || undefined
      });

      toast.success("Profile updated successfully!");
      if (newEmail) {
        setAdminEmail(newEmail.trim());
      }

      setNewEmail("");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err: any) {
      toast.error(err.message || "Failed to update profile settings");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in text-left">
      <div>
        <h2 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
          <UserCog className="h-8 w-8 text-brand-primary" />
          <span>Profile Settings</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Manage your account credentials, email aliases, and security parameters.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 md:p-8 space-y-6 shadow-sm">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="current-email">Current Account Email</Label>
            <Input id="current-email" type="email" value={currentEmail} disabled className="bg-muted/50 border-border/60 text-muted-foreground cursor-not-allowed" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-email">New Account Email (Optional)</Label>
            <Input id="new-email" type="email" placeholder="new-alias@ugurcanuzunkaya.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="border-border/60" />
            <p className="text-xs text-muted-foreground">Must still be an @ugurcanuzunkaya.com address.</p>
          </div>

          <hr className="border-border/60 my-6" />

          <h3 className="text-md font-bold text-foreground flex items-center gap-2">
            <KeyRound className="h-4.5 w-4.5 text-brand-primary" />
            <span>Update Password</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New Password</Label>
              <Input id="new-password" type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="border-border/60" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <Input id="confirm-new-password" type="password" placeholder="••••••••" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className="border-border/60" />
            </div>
          </div>
          <div className="p-3 bg-muted/40 border border-border/60 rounded-xl flex items-start gap-2.5 text-xs text-muted-foreground">
            <Info className="h-4 w-4 text-brand-primary shrink-0 mt-0.5" />
            <span>
              Passwords must be at least 8 characters long, containing uppercase, lowercase, numbers, and special characters. Changing the password will sign out all other devices.
            </span>
          </div>

          <hr className="border-border/60 my-6" />

          <div className="space-y-1.5">
            <Label htmlFor="current-password">Current Password (Required to Apply Changes)</Label>
            <Input id="current-password" type="password" placeholder="••••••••" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="border-border/60" required />
          </div>
        </div>

        <Button type="submit" disabled={updating} className="w-full md:w-auto bg-brand-primary text-white hover:bg-brand-primary/95 font-semibold h-11 px-8">
          {updating ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating...</>
          ) : (
            "Save Profile Changes"
          )}
        </Button>
      </form>
    </div>
  );
}

export default AdminProfileTab;
